import { useState } from "react";
import { BookText, ChevronRight, Search } from "lucide-react";
import type {
  Term,
  TermGroupKind,
  TermsResponse,
} from "@patent-studio/api-types";
import { IconButton } from "@/components/ui/button";
import { DisclosureHeader, useDisclosure } from "@/components/ui/Disclosure";
import { EmptyState } from "@/components/ui/EmptyState";
import { IconTile } from "@/components/ui/IconTile";
import { Input } from "@/components/ui/input";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Segmented } from "@/components/ui/Segmented";
import { StatusPill } from "@/components/ui/StatusPill";
import { plural } from "@/lib/plural";
import { cn } from "@/lib/utils";
import {
  GroupEntries,
  READINGS,
  type GoToSource,
  type PendingTerm,
  type TermsLayout,
  type TermsReading,
} from "./TermEntries";
import { flagOf, type SeenTerms } from "./terms-seen";

const GROUP_LABEL: Record<TermGroupKind, string> = {
  components: "Numbered components",
  elements: "Invention elements",
  positions: "Novelty positions",
};

const INTRO =
  "The terms Patent Studio will use in the draft. More terms may be added as planning continues. To change a term, edit it where it was defined.";

const RENAME_HELP =
  "To change the word Patent Studio uses, rename the term where it was defined: a numbered component title in Figures, an element in Invention or a position in Novelty. Patent Studio will then use your wording throughout the draft.";

const NO_TERMS =
  "No terms yet. Terms are added as you name numbered components in Figures, define invention elements and set novelty positions.";

/** Whether the search finds the term: in its name, or in the words it is defined by, or as its whole numeral ("12", "(12)"). */
function matches(term: Term, query: string): boolean {
  if (query === "") return true;
  const numeral = query.replace(/[()\s]/g, "");
  return (
    term.name.toLowerCase().includes(query) ||
    (term.definition ?? "").toLowerCase().includes(query) ||
    (term.numeral !== null && term.numeral.toLowerCase() === numeral)
  );
}

/** One group: its heading folds it, except while searching, when every match shows. */
function TermGroupSection({
  kind,
  terms,
  seen,
  folded,
  onFold,
  onGo,
  pending,
  layout,
  reading,
}: {
  kind: TermGroupKind;
  terms: Term[];
  seen: SeenTerms | null;
  folded: boolean;
  onFold: () => void;
  onGo: GoToSource;
  pending: PendingTerm | null;
  layout: TermsLayout;
  reading: TermsReading;
}) {
  const fold = useDisclosure(true, {
    open: !folded,
    setOpen: onFold,
  });
  return (
    <section>
      <DisclosureHeader
        trigger={fold.trigger}
        open={fold.open}
        title={<MicroLabel size="sm">{GROUP_LABEL[kind]}</MicroLabel>}
        meta={
          <StatusPill tone="neutral" size="sm">
            {terms.length}
          </StatusPill>
        }
        className="mx-1 mt-3.5 mb-1.5"
      />
      <GroupEntries
        kind={kind}
        terms={terms}
        seen={seen}
        folded={folded}
        panel={fold.panel}
        onGo={onGo}
        pending={pending}
        layout={layout}
        reading={reading}
      />
    </section>
  );
}

function countLine(
  view: TermsResponse,
  seen: SeenTerms | null,
  query: string,
  shown: number,
): string {
  const total = view.groups.reduce((sum, group) => sum + group.terms.length, 0);
  if (query !== "")
    return shown === 0
      ? `No match among ${String(total)} terms`
      : `Patent Studio uses ${String(shown)} of ${String(total)} terms matching "${query}"`;
  const flagged = view.groups.reduce(
    (sum, group) =>
      sum +
      group.terms.filter((term) => flagOf(group.kind, term, seen) !== null)
        .length,
    0,
  );
  const terms = plural(total, "term");
  return flagged === 0
    ? terms
    : `${terms} · ${String(flagged)} changed since you last opened this`;
}

/**
 * The two places the list is drawn, each with its own count line and scroll. Each scroll is the
 * box of what the list places outside the flow, and a scroll past its end stops there rather than
 * moving the page behind it.
 */
const LIST_FRAME: Record<TermsLayout, { count: string; scroll: string }> = {
  dock: {
    count:
      "border-b border-border px-4 py-2 text-[11px] text-subtle-foreground",
    scroll:
      "relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pt-1 pb-5",
  },
  card: {
    count: "py-2 text-[11px] text-subtle-foreground",
    scroll:
      "relative max-h-[520px] overflow-y-auto overscroll-contain border-t border-border px-0.5 pb-1",
  },
};

/** What a search that finds nothing says: Praxis does not use the word, and how to change the word it does use. */
function NotUsed({ word }: { word: string }) {
  return (
    <EmptyState className="m-1 text-[12px] leading-normal">
      <div className="text-left">
        <p>
          <b className="text-foreground">
            Patent Studio is not using "{word}" as a term.
          </b>{" "}
          It may use a different word for the same thing. Clear the search and
          check the list for it.
        </p>
        <p className="mt-3">{RENAME_HELP}</p>
      </div>
    </EmptyState>
  );
}

/** The groups that match the search, and the count line over them. */
function TermList({
  view,
  seen,
  query,
  onGo,
  pending,
  layout,
  reading,
}: {
  view: TermsResponse;
  seen: SeenTerms | null;
  query: string;
  onGo: GoToSource;
  pending: PendingTerm | null;
  layout: TermsLayout;
  reading: TermsReading;
}) {
  const [folded, setFolded] = useState<ReadonlySet<TermGroupKind>>(
    () => new Set(),
  );
  const needle = query.trim().toLowerCase();
  const groups = view.groups
    .map((group) => ({
      ...group,
      terms: group.terms.filter((term) => matches(term, needle)),
    }))
    .filter((group) => group.terms.length > 0);
  const shown = groups.reduce((sum, group) => sum + group.terms.length, 0);
  let body;
  if (view.groups.length === 0) {
    body = <EmptyState className="m-1 text-[12px]">{NO_TERMS}</EmptyState>;
  } else if (groups.length === 0) {
    body = <NotUsed word={query.trim()} />;
  } else {
    body = groups.map((group) => (
      <TermGroupSection
        key={group.kind}
        kind={group.kind}
        terms={group.terms}
        seen={seen}
        onGo={onGo}
        pending={pending}
        layout={layout}
        reading={reading}
        folded={needle === "" && folded.has(group.kind)}
        onFold={() => {
          setFolded((was) => {
            const next = new Set(was);
            if (!next.delete(group.kind)) next.add(group.kind);
            return next;
          });
        }}
      />
    ));
  }
  return (
    <>
      <p className={LIST_FRAME[layout].count}>
        {countLine(view, seen, query.trim(), shown)}
      </p>
      <div className={LIST_FRAME[layout].scroll}>{body}</div>
    </>
  );
}

/**
 * Every term the draft will use, searchable by its words or its whole reference
 * numeral, by group, read as chips ("Terms only") or with each term's details:
 * what the Terminology panel and Generate's review card both list. `seen` flags
 * the terms that differ from it; the review passes none.
 */
export function TermsBrowser({
  view,
  seen,
  onGo,
  pending = null,
  layout,
}: {
  view: TermsResponse | undefined;
  seen: SeenTerms | null;
  onGo: GoToSource;
  pending?: PendingTerm | null;
  layout: TermsLayout;
}) {
  const [query, setQuery] = useState("");
  const [reading, setReading] = useState<TermsReading>("detail");
  const search = (
    <div
      className={cn(
        "relative",
        layout === "dock" ? "mx-4 mt-3" : "max-w-[420px] min-w-[240px] flex-1",
      )}
    >
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-subtle-foreground"
      />
      <Input
        type="search"
        aria-label="Search terms"
        placeholder="Check whether Patent Studio uses a word or numeral"
        size="roomy"
        autoComplete="off"
        className="pl-8"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
    </div>
  );
  const switcher = (
    <Segmented
      label="Table view"
      choices={READINGS}
      current={reading}
      onChoose={setReading}
    />
  );
  return (
    <>
      {layout === "dock" ? (
        <>
          {search}
          <div className="mx-4 mt-2.5">{switcher}</div>
        </>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          {search}
          {switcher}
        </div>
      )}
      {view === undefined ? (
        <p className="px-4 py-2 text-[12px] text-muted-foreground">
          Reading the terms…
        </p>
      ) : (
        <TermList
          view={view}
          seen={seen}
          query={query}
          onGo={onGo}
          pending={pending}
          layout={layout}
          reading={reading}
        />
      )}
    </>
  );
}

/**
 * The Terminology panel: every term the draft will use, by group, searchable
 * by its words or its reference numeral. It edits nothing; a term is changed
 * where it was defined.
 *
 * `seen` is what the attorney saw when they last opened it, and a term that
 * differs from it is flagged.
 */
export function TermsPanel({
  view,
  seen,
  onClose,
  onGo,
  pending = null,
}: {
  view: TermsResponse | undefined;
  seen: SeenTerms | null;
  onClose: () => void;
  onGo: GoToSource;
  pending?: PendingTerm | null;
}) {
  return (
    <section aria-label="Terminology" className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-border py-3 pr-3.5 pl-4">
        <IconTile size="sm">
          <BookText />
        </IconTile>
        <h2 className="flex-1 text-[14px] font-bold">Terminology</h2>
        <IconButton
          variant="secondary"
          aria-label="Close panel"
          title="Close panel"
          onClick={onClose}
        >
          <ChevronRight />
        </IconButton>
      </header>
      <p className="px-4 pt-3 text-[12px] leading-normal text-pretty text-muted-foreground">
        {INTRO}
      </p>
      <TermsBrowser
        view={view}
        seen={seen}
        onGo={onGo}
        pending={pending}
        layout="dock"
      />
    </section>
  );
}
